import {Component} from 'panel';

import {NAVIGATION_KEY_CODES} from '../../util/dom';
import {registerMPElement} from '../../util/register-element';
import {ConfirmAttrs, THEME} from './types';

import template from './index.jade';
import css from './index.styl?inline';
import {promisifyModal} from '../modal/utils';

interface ConfirmState {
  inputValue?: string;
}

export class Confirm extends Component<ConfirmState> {
  inputElm: HTMLInputElement;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'hide-cancel-button': `boolean`,
      'cancel-button-label': {type: `string`, default: `Cancel`},
      closeable: `boolean`,
      'confirm-button-disabled': `boolean`,
      'confirm-button-label': {type: `string`},
      open: `boolean`,
      title: `string`,
      description: `string`,
      theme: {
        type: `string`,
        default: THEME.CONFIRM,
        enum: Object.values(THEME),
      },
      'show-input': {
        type: `boolean`,
        default: false,
        description: `Presents a required input field to the user. The result is provided in the confirm event: details.inputValue.`,
      },
      'initial-input-value': {
        type: `string`,
        default: ``,
        description: `Provides an initial value for the input field.`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      defaultState: {
        inputValue: ``,
      },

      helpers: {
        modalChanged: ({detail}) => {
          this.dispatchEvent(new CustomEvent(`change`, {detail}));
          if (detail.state === `open` && this.inputElm) {
            requestAnimationFrame(() => {
              this.inputElm.focus();
              this.inputElm.select();
            });
          }
        },

        cancel: () => {
          const ev = new CustomEvent(`submit`, {cancelable: true, detail: {action: `cancel`}});
          this.dispatchEvent(ev);
          if (!ev.defaultPrevented) {
            this.setAttribute(`open`, `false`);
          }
        },

        confirm: () => {
          this.confirm();
        },

        isConfirmButtonDisabled: (): boolean => {
          return this.attr(`confirm-button-disabled`) || (this.attr(`show-input`) && !this.state.inputValue);
        },

        // Input helpers

        changeInputValue: (ev) => {
          ev.stopPropagation();
          this.update({inputValue: ev.target.value});
        },

        commitInputValue: ({keyCode}) => {
          if (keyCode === NAVIGATION_KEY_CODES.ENTER && !this.helpers.isConfirmButtonDisabled()) {
            this.helpers.confirm();
          }
        },

        onInputInserted: (vnode) => (this.inputElm = vnode.elm),
      },
    };
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    switch (name) {
      case `initial-input-value`:
        this.update({inputValue: newValue});
        break;
    }
  }

  confirm() {
    const detail = {
      action: `confirm`,
      inputValue: this.state.inputValue,
    };
    const ev = new CustomEvent(`submit`, {cancelable: true, detail});
    this.dispatchEvent(ev);
    if (!ev.defaultPrevented) {
      this.setAttribute(`open`, `false`);
    }
  }
}

export default registerMPElement(`mp-confirm`, Confirm);

export async function confirm(attrs: ConfirmAttrs): Promise<boolean> {
  const closeEv = await promisifyModal({tagName: `mp-confirm`, attrs, closeEvents: [`submit`]});
  return closeEv.detail.action === `confirm`;
}

export function confirmDelete(attrs: ConfirmAttrs): Promise<boolean> {
  return confirm({...attrs, theme: THEME.DELETE});
}

export async function prompt(attrs: ConfirmAttrs): Promise<{didConfirm: boolean; inputValue: string}> {
  const closeEv = await promisifyModal({
    tagName: `mp-confirm`,
    attrs: {closeable: true, 'confirm-button-label': `Save`, ...attrs, 'show-input': true},
    closeEvents: [`submit`],
  });

  return {
    didConfirm: closeEv.type === `submit` && closeEv.detail.action === `confirm`,
    inputValue: closeEv.type === `submit` ? closeEv.detail.inputValue : null,
  };
}
